import React from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Mic, Square, Loader2, Save, FileText, Calculator } from "lucide-react";

/**
 * Panel de "Instrucciones para la IA" del expediente.
 *
 * Hay DOS bloques INDEPENDIENTES y separados intencionadamente:
 *   - "Causas y circunstancias": instrucciones que solo afectan a la
 *     redacción de la narrativa.
 *   - "Valoración sugerida": instrucciones que solo afectan a la generación
 *     de la tabla de valoración (partidas, precios, criterios de reparación).
 *
 * Las instrucciones de un bloque NO se inyectan en el otro: si el perito
 * escribe "aplica solo zona dañada del parquet" en valoración, esto no se
 * redactará en causas; si escribe en causas "menciona que el vecino vio el
 * agua a las 8h", esto no se utilizará para construir partidas.
 *
 * Componente presentacional puro: estado y handlers viven en el padre.
 */
export function AiInstructionsPanel({
  // Causas
  causasDraft,
  onCausasDraftChange,
  causasMeta,            // { updated_at, updated_by }
  causasRecording,
  causasTranscribing,
  onCausasStartRecording,
  onCausasStopRecording,
  // Valoración
  valoracionDraft,
  onValoracionDraftChange,
  valoracionMeta,        // { updated_at, updated_by }
  valoracionRecording,
  valoracionTranscribing,
  onValoracionStartRecording,
  onValoracionStopRecording,
  // Común
  saving,
  onSave,
}) {
  return (
    <div className="bg-white border-2 border-[#FFD9C7] rounded-[14px] p-6" data-testid="ai-instructions-block">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Sparkles className="h-4 w-4 text-[#D85A30]" />
        <div className="font-medium text-[#2C2C2A]">Instrucciones para la IA</div>
        <span className="ml-auto text-[10px] uppercase tracking-wide text-[#D85A30] font-semibold">Permanente</span>
      </div>
      <p className="text-xs text-[#7A786F] mb-4">
        Hay <strong>dos bloques independientes</strong>. Las instrucciones de
        valoración <em>no</em> se redactan en causas, y las instrucciones de
        causas <em>no</em> alteran la valoración.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Bloque CAUSAS */}
        <InstructionBlock
          icon={<FileText className="h-3.5 w-3.5 text-[#2E5AAC]" />}
          accentColor="#2E5AAC"
          title="Causas y circunstancias"
          hint="Solo afecta a la redacción de la narrativa."
          placeholder="Ej.: Menciona que el agua venía del piso superior. No incluyas opiniones del asegurado sobre culpabilidad. Sé conciso (máx. 8 líneas)."
          draft={causasDraft}
          onDraftChange={onCausasDraftChange}
          meta={causasMeta}
          recording={causasRecording}
          transcribing={causasTranscribing}
          onStart={onCausasStartRecording}
          onStop={onCausasStopRecording}
          testid="ai-instructions-causas"
          saving={saving}
        />
        {/* Bloque VALORACIÓN */}
        <InstructionBlock
          icon={<Calculator className="h-3.5 w-3.5 text-[#1D9E75]" />}
          accentColor="#1D9E75"
          title="Valoración sugerida"
          hint="Solo afecta a las partidas y precios. La IA NO redactará nada de esto en causas."
          placeholder="Ej.: Aplica franquicia de 300 €. No propongas reposición integral del suelo, solo la zona dañada. Usa el baremo Hogar Flexible XXL. Si la cobertura no es clara, márcala como NO CUBIERTA."
          draft={valoracionDraft}
          onDraftChange={onValoracionDraftChange}
          meta={valoracionMeta}
          recording={valoracionRecording}
          transcribing={valoracionTranscribing}
          onStart={onValoracionStartRecording}
          onStop={onValoracionStopRecording}
          testid="ai-instructions-valoracion"
          saving={saving}
        />
      </div>

      <div className="flex items-center justify-end mt-4">
        <Button size="sm" onClick={onSave}
          disabled={saving || causasRecording || valoracionRecording}
          className="bg-[#D85A30] hover:bg-[#C04D27] text-white" data-testid="ai-instructions-save-btn">
          {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
          Guardar ambos bloques
        </Button>
      </div>
    </div>
  );
}

function InstructionBlock({
  icon, accentColor, title, hint, placeholder,
  draft, onDraftChange,
  meta,
  recording, transcribing,
  onStart, onStop,
  testid,
  saving,
}) {
  const len = (draft || "").length;
  return (
    <div className="border border-[#E5E2D8] rounded-[12px] p-3" data-testid={`${testid}-block`}>
      <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
        {icon}
        <div className="text-[12px] font-semibold tracking-tight" style={{ color: accentColor }}>{title}</div>
        {meta?.updated_at && (
          <span className="text-[10px] text-[#7A786F] font-mono">
            act. {new Date(meta.updated_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}
          </span>
        )}
      </div>
      <p className="text-[11px] text-[#7A786F] mb-2">{hint}</p>
      <textarea
        value={draft || ""}
        onChange={(e) => onDraftChange(e.target.value)}
        rows={5}
        maxLength={6000}
        placeholder={placeholder}
        className="w-full bg-[#FBFAF6] text-[#2C2C2A] text-sm leading-relaxed p-3 rounded-[10px] border border-[#E5E2D8] focus:border-[#D85A30] outline-none resize-y"
        data-testid={`${testid}-textarea`}
      />
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className="text-[10px] text-[#7A786F]">{len}/6.000</span>
        {!recording ? (
          <Button size="sm" variant="outline" onClick={onStart}
            disabled={transcribing || saving}
            className="ml-auto border-[#E5E2D8] h-7 px-2" data-testid={`${testid}-mic-btn`}>
            {transcribing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Mic className="h-3 w-3 mr-1" />}
            {transcribing ? "Transcribiendo…" : "Dictar"}
          </Button>
        ) : (
          <Button size="sm" onClick={onStop}
            className="ml-auto bg-[#B43F3F] hover:bg-[#9A3535] text-white h-7 px-2" data-testid={`${testid}-stop-btn`}>
            <Square className="h-3 w-3 mr-1" /> Detener
          </Button>
        )}
      </div>
    </div>
  );
}
